import React, { useEffect, useRef, useState } from "react";
import { MealPlanData } from "types/mealPlans";

import Button from "components/atoms/Button";

import {
  exportToCSV,
  exportToJSON,
  exportToPDF,
  exportToStyledPDF,
} from "utils/exportUtils";

interface ExportButtonsProps {
  mealPlanData: MealPlanData;
  onImportMealPlan?: (importedData: MealPlanData) => void;
}

/**
 * A component that displays buttons for exporting meal plan data in various formats
 */
const ExportButtons: React.FC<ExportButtonsProps> = ({
  mealPlanData,
  onImportMealPlan,
}) => {
  const [isExporting, setIsExporting] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef<boolean>(true);
  const fileReaderRef = useRef<FileReader | null>(null);

  // Set up cleanup when component unmounts
  useEffect(() => {
    // Component is mounted
    isMountedRef.current = true;

    // Cleanup function
    return () => {
      // Component is unmounting
      isMountedRef.current = false;

      // Abort any pending FileReader operations
      if (fileReaderRef.current) {
        fileReaderRef.current.abort();
        fileReaderRef.current = null;
      }
    };
  }, []);

  const handleExport = async (
    format: string,
    exportFn: (data: MealPlanData) => Promise<void>
  ) => {
    setIsExporting(format);
    try {
      await exportFn(mealPlanData);
    } catch (error) {
      alert(`Failed to export to ${format}. Please try again.`);
    } finally {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setIsExporting(null);
      }
    }
  };

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !onImportMealPlan) return;

    setIsImporting(true);

    // Create new FileReader and store the reference
    const reader = new FileReader();
    fileReaderRef.current = reader;

    reader.onload = (e) => {
      // Only proceed if component is still mounted
      if (!isMountedRef.current) return;

      try {
        const content = e.target?.result as string;
        const importedData = JSON.parse(content) as MealPlanData;

        // Basic validation of the imported data
        if (
          !importedData ||
          !importedData.mealPlanDays ||
          !Array.isArray(importedData.mealPlanDays)
        ) {
          throw new Error("Invalid meal plan data format");
        }

        onImportMealPlan(importedData);
      } catch (error) {
        alert("The selected file doesn't contain valid meal plan data.");
      } finally {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setIsImporting(false);
          // Reset the file input
          if (event.target) {
            event.target.value = "";
          }
        }
        fileReaderRef.current = null;
      }
    };

    reader.onerror = () => {
      // Only proceed if component is still mounted
      if (!isMountedRef.current) return;

      alert("Error reading the selected file.");
      setIsImporting(false);
      fileReaderRef.current = null;
    };

    reader.readAsText(file);
  };

  return (
    <div className="mt-8 pt-6 border-t border-gray-200">
      <h3 className="text-lg font-medium mb-4">Export Meal Plan</h3>
      <div className="flex flex-wrap gap-4">
        <Button
          text={isExporting === "json" ? "Exporting..." : "Export as JSON"}
          onClick={() => handleExport("json", exportToJSON)}
          disabled={isExporting !== null || isImporting}
          isLoading={isExporting === "json"}
        />
        <Button
          text={isExporting === "csv" ? "Exporting..." : "Export as CSV"}
          onClick={() => handleExport("csv", exportToCSV)}
          disabled={isExporting !== null || isImporting}
          isLoading={isExporting === "csv"}
        />
        <Button
          text={isExporting === "pdf" ? "Exporting..." : "Export as PDF"}
          onClick={() => handleExport("pdf", exportToPDF)}
          disabled={isExporting !== null || isImporting}
          isLoading={isExporting === "pdf"}
        />
        <Button
          text={
            isExporting === "styledPdf"
              ? "Exporting..."
              : "Export as Styled PDF"
          }
          onClick={() => handleExport("styledPdf", exportToStyledPDF)}
          disabled={isExporting !== null || isImporting}
          isLoading={isExporting === "styledPdf"}
        />

        {onImportMealPlan && (
          <>
            <div className="border-l border-gray-300 h-8 mx-1" />
            <Button
              text={isImporting ? "Importing..." : "Import Meal Plan"}
              onClick={handleImportClick}
              disabled={isExporting !== null || isImporting}
              isLoading={isImporting}
              outlined
            />
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              className="hidden"
              disabled={isExporting !== null || isImporting}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ExportButtons;
